'use client'
import Link from 'next/link'
import { Logo } from '@/components/logo'
import { useAuth } from '@/contexts/auth-context'


import React, { useState, useRef } from 'react'
import { cn } from '@/lib/utils'
import { motion, useMotionValue, AnimatePresence, type Variants } from "framer-motion"

const menuItems = [
    { name: 'Features', href: '#features' },
    { name: 'Events', href: '#events' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'About', href: '#about' },
]

// Curved menu interfaces and components
interface iNavItem {
    heading: string;
    href: string;
    subheading?: string;
}

interface iNavLinkProps extends iNavItem {
    setIsActive: (isActive: boolean) => void;
    index: number;
}

interface iCurvedNavbarProps {
    setIsActive: (isActive: boolean) => void;
    navItems: iNavItem[];
}

const MENU_SLIDE_ANIMATION: Variants = {
    initial: { x: "calc(100% + 100px)" },
    enter: { x: "0", transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
    exit: {
        x: "calc(100% + 100px)",
        transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
    },
};

const NavLink: React.FC<iNavLinkProps> = ({
    heading,
    href,
    setIsActive,
    index,
}) => {
    const ref = useRef<HTMLAnchorElement | null>(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const handleMouseMove = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
        const rect = ref.current!.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        x.set(mouseX / rect.width - 0.5);
        y.set(mouseY / rect.height - 0.5);
    };

    const handleClick = () => {
        return setIsActive(false);
    };

    return (
        <motion.div
            onClick={handleClick}
            initial="initial"
            whileHover="whileHover"
            className="group relative flex items-center justify-between border-b border-foreground/30 py-4 transition-colors duration-500 md:py-8 uppercase"
        >
            <Link ref={ref} onMouseMove={handleMouseMove} href={href}>
                <div className="relative flex items-start">
                    <span className="text-foreground transition-colors duration-500 text-4xl font-thin mr-2">
                        {index}.
                    </span>
                    <div className="flex flex-row gap-2">
                        <motion.span
                            variants={{
                                initial: { x: 0 },
                                whileHover: { x: -16 },
                            }}
                            transition={{
                                type: "spring",
                                staggerChildren: 0.075,
                                delayChildren: 0.25,
                            }}
                            className="relative z-10 block text-4xl font-extralight text-foreground transition-colors duration-500 md:text-4xl"
                        >
                            {heading.split("").map((letter, i) => {
                                return (
                                    <motion.span
                                        key={i}
                                        variants={{
                                            initial: { x: 0 },
                                            whileHover: { x: 16 },
                                        }}
                                        transition={{ type: "spring" }}
                                        className="inline-block"
                                    >
                                        {letter}
                                    </motion.span>
                                );
                            })}
                        </motion.span>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
};

const Curve: React.FC = () => {
    const [windowHeight, setWindowHeight] = useState(800); // Default height

    React.useEffect(() => {
        const updateHeight = () => setWindowHeight(window.innerHeight);
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const initialPath = `M100 0 L200 0 L200 ${windowHeight} L100 ${windowHeight} Q-100 ${windowHeight / 2} 100 0`;
    const targetPath = `M100 0 L200 0 L200 ${windowHeight} L100 ${windowHeight} Q100 ${windowHeight / 2} 100 0`;

    const curve: Variants = {
        initial: { d: initialPath },
        enter: {
            d: targetPath,
            transition: { duration: 1, ease: [0.76, 0, 0.24, 1] },
        },
        exit: {
            d: initialPath,
            transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
        },
    };

    return (
        <svg
            className="absolute top-0 -left-[99px] w-[100px] stroke-none h-full fill-white dark:fill-[#141414]"
        >
            <motion.path
                variants={curve}
                initial="initial"
                animate="enter"
                exit="exit"
            />
        </svg>
    );
};

const CurvedNavbar: React.FC<iCurvedNavbarProps & { footer?: React.ReactNode }> = ({
    setIsActive,
    navItems,
    footer
}) => {
    return (
        <motion.div
            variants={MENU_SLIDE_ANIMATION}
            initial="initial"
            animate="enter"
            exit="exit"
            className="h-[100dvh] w-screen max-w-screen-sm fixed right-0 top-0 z-50 bg-white dark:bg-[#141414] border-l border-border"
        >
            {/* Close Button (X) */}
            <div className="absolute top-5 right-5 z-60">
                <button
                    onClick={() => setIsActive(false)}
                    className="w-12 h-12 rounded-full bg-foreground/10 hover:bg-foreground/20 transition-colors duration-200 flex items-center justify-center"
                    aria-label="Close menu"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-foreground"
                    >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>

            {/* Menu Content with Padding All Around */}
            <div className="h-full pt-20 pb-8 px-8 flex flex-col justify-between">
                <div className="flex flex-col text-5xl gap-3 mt-0">
                    <div className="text-foreground border-b border-foreground/30 uppercase text-sm mb-4">
                        <p>Navigation</p>
                    </div>
                    <section className="bg-transparent mt-0">
                        <div className="mx-auto max-w-7xl">
                            {navItems.map((item, index) => {
                                return (
                                    <NavLink
                                        key={item.href}
                                        {...item}
                                        setIsActive={setIsActive}
                                        index={index + 1}
                                    />
                                );
                            })}
                        </div>
                    </section>
                </div>
                {footer}
            </div>
            <Curve />
        </motion.div>
    );
};

export const HeroHeader = () => {
    const [isActive, setIsActive] = useState(false)
    const { user, profile, loading } = useAuth()
    // Wire up scroll listener for tests and subtle styling changes
    React.useEffect(() => {
      const onScroll = () => {
        // no-op for now; tests only assert listener presence
      }
      window.addEventListener('scroll', onScroll)
      return () => window.removeEventListener('scroll', onScroll)
    }, [])


    const handleMenuClick = () => {
        setIsActive(!isActive);
    };

    return (
        <>
        <header className="fixed top-0 left-0 w-full z-[100] p-2 sm:p-4">
            <div className="bg-white border-2 sm:border-4 border-black rounded-lg p-2 sm:p-3 transform -rotate-1 hover:rotate-0 transition-all duration-200 max-w-7xl mx-auto shadow-[4px_4px_0px_0px_#000000] sm:shadow-[8px_8px_0px_0px_#000000]">
                <div className="flex items-center justify-between gap-4">
                    <Link
                        href="/"
                        aria-label="home"
                        className="bg-[#facc15] border-2 sm:border-4 border-black rounded p-1 sm:p-2 transform rotate-2 hover:rotate-0 transition-all duration-200 hover:bg-black hover:text-white">
                        <Logo
                          height={40}
                          className="w-[164px] sm:w-[160px] md:w-[208px]"
                          fit="contain"
                          data-testid="logo"
                        />
                    </Link>


                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-2">
                        {menuItems.map((item, index) => {
                            return (
                                <Link
                                    key={index}
                                    href={item.href}
                                    className={cn(
                                        "text-black font-black text-lg px-4 py-2 border-4 rounded transition-all duration-200 uppercase tracking-wide",
                                        index % 2 === 0
                                            ? "border-transparent hover:border-black hover:bg-[#1e74e6] hover:text-white transform -rotate-1 hover:rotate-0"
                                            : "border-transparent hover:border-black hover:bg-[#17cbbf] transform rotate-1 hover:rotate-0"
                                    )}>
                                    {item.name}
                                </Link>
                            )
                        })}
                    </nav>

                    {/* Auth Buttons */}
                    <div className="hidden lg:flex items-center gap-3 min-w-[200px] justify-end">
                        {loading ? (
                            <div className="bg-gray-200 border-4 border-black rounded px-5 py-2 animate-pulse">
                                <div className="w-20 h-6 bg-gray-300 rounded"></div>
                            </div>
                        ) : (
                            user ?
                                <div className="flex items-center gap-3">
                                    <span className="text-black font-bold text-sm hidden xl:block">
                                        Welcome, {profile?.full_name?.split(' ')[0] || 'User'}!
                                    </span>
                                    <Link
                                        href="/dashboard"
                                        className="bg-[#1553c5] border-4 border-black rounded px-5 py-2 font-black text-white text-lg uppercase tracking-wide transform rotate-1 hover:rotate-0 hover:bg-[#facc15] hover:text-black transition-all duration-200">
                                        Dashboard
                                    </Link>
                                </div>
                            :
                                <>
                                    <Link
                                        href="/login"
                                        className="bg-[#60a5fa] border-4 border-black rounded px-5 py-2 font-black text-black text-lg uppercase tracking-wide transform -rotate-1 hover:rotate-0 hover:bg-black hover:text-white transition-all duration-200">
                                        Login
                                    </Link>
                                    <Link
                                        href="/signup"
                                        className="bg-[#1553c5] border-4 border-black rounded px-5 py-2 font-black text-white text-lg uppercase tracking-wide transform rotate-1 hover:rotate-0 hover:bg-[#facc15] hover:text-black transition-all duration-200">
                                        Sign Up
                                    </Link>
                                </>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={handleMenuClick}
                        aria-label={isActive ? 'Close Menu' : 'Open Menu'}
                        className="lg:hidden bg-[#17cbbf] border-2 sm:border-4 border-black rounded p-2 sm:p-3 transform rotate-1 hover:rotate-0 hover:bg-black hover:text-white transition-all duration-200">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 flex flex-col justify-around">
                            <span className={cn("block h-0.5 sm:h-1 w-full bg-current transition-transform duration-300 rounded-sm", isActive ? "rotate-45 translate-y-[7px] sm:translate-y-[8px]" : "")}></span>
                            <span className={cn("block h-0.5 sm:h-1 w-full bg-current transition-opacity duration-300 rounded-sm", isActive ? "opacity-0" : "")}></span>
                            <span className={cn("block h-0.5 sm:h-1 w-full bg-current transition-transform duration-300 rounded-sm", isActive ? "-rotate-45 -translate-y-[7px] sm:-translate-y-[8px]" : "")}></span>
                        </div>
                    </button>
                </div>
            </div>

        </header>

        {/* Mobile Menu Panel */}
        <AnimatePresence>
            {isActive && (
                <CurvedNavbar
                  setIsActive={setIsActive}
                  navItems={menuItems.map((m) => ({ heading: m.name, href: m.href }))}
                  footer={
                    <div className="flex flex-col gap-6 mt-10 w-full max-w-sm">
                      
                      {loading ? (
                        <div className="px-6 py-4 border-4 border-black bg-gray-200 rounded animate-pulse">
                          <div className="w-24 h-6 bg-gray-300 rounded mx-auto"></div>
                        </div>
                      ) : user ? (
                        <>
                          <div className="text-center px-4 py-2 bg-[#facc15] border-4 border-black rounded font-black text-black text-lg transform -rotate-1">
                            Welcome, {profile?.full_name?.split(' ')[0] || 'User'}! 👋
                          </div>
                          <Link href="/dashboard" onClick={() => setIsActive(false)} className="px-6 py-4 text-white font-black text-xl text-center border-4 border-black bg-[#1e74e6] rounded uppercase tracking-wide transform rotate-1 hover:rotate-0 hover:bg-[#facc15] hover:text-black transition-all duration-200">
                            Dashboard
                          </Link>
                        </>
                      ) : (
                        <>
                          <Link href="/login" onClick={() => setIsActive(false)} className="px-6 py-4 text-black font-black text-xl text-center border-4 border-black bg-[#60a5fa] rounded uppercase tracking-wide transform rotate-1 hover:rotate-0 hover:bg-black hover:text-white transition-all duration-200">
                            Login
                          </Link>
                          <Link href="/signup" onClick={() => setIsActive(false)} className="px-6 py-4 text-white font-black text-xl text-center border-4 border-black bg-[#1e74e6] rounded uppercase tracking-wide transform -rotate-1 hover:rotate-0 hover:bg-[#facc15] hover:text-black transition-all duration-200">
                            Sign Up
                          </Link>
                        </>
                      )}
                    </div>
                  }
                />
            )}
        </AnimatePresence>
        </>
    )
}
